import React, { useState, useEffect } from 'react';
import {
  Box,
  Alert,
  Snackbar,
  Typography,
  Button,
  Chip,
  IconButton,
  Collapse,
  LinearProgress,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import {
  WifiOff as OfflineIcon,
  Wifi as OnlineIcon,
  Sync as SyncIcon,
  Close as CloseIcon,
  CloudOff as CloudOffIcon,
  CloudDone as CloudDoneIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';

// ===== ТИПЫ =====

/** Network Information API (не в стандартном Navigator) */
interface NetworkInformation {
  type?: string;
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
  addEventListener?(type: string, listener: () => void): void;
  removeEventListener?(type: string, listener: () => void): void;
}

interface NavigatorWithConnection extends Navigator {
  connection?: NetworkInformation;
  mozConnection?: NetworkInformation;
  webkitConnection?: NetworkInformation;
}

function getConnection(): NetworkInformation | undefined {
  const nav = navigator as NavigatorWithConnection;
  return nav.connection ?? nav.mozConnection ?? nav.webkitConnection;
}

interface OfflineData {
  id: string;
  type: 'create' | 'update' | 'delete';
  entity: string;
  data: any;
  timestamp: number;
  retryCount: number;
  maxRetries: number;
}

interface OfflineModeProps {
  onSync?: (data: OfflineData[]) => Promise<void>;
  maxRetries?: number;
  syncInterval?: number;
  showDetails?: boolean;
}

interface NetworkStatus {
  isOnline: boolean;
  connectionType?: string;
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
}

// ===== ХУКИ =====

/**
 * 🌐 Hook для отслеживания статуса сети
 */
const useNetworkStatus = (): NetworkStatus => {
  const [networkStatus, setNetworkStatus] = useState<NetworkStatus>({
    isOnline: navigator.onLine
  });

  useEffect(() => {
    const updateNetworkStatus = () => {
      const conn = getConnection();
      setNetworkStatus({
        isOnline: navigator.onLine,
        ...(conn?.type != null && { connectionType: conn.type }),
        ...(conn?.effectiveType != null && { effectiveType: conn.effectiveType }),
        ...(conn?.downlink != null && { downlink: conn.downlink }),
        ...(conn?.rtt != null && { rtt: conn.rtt }),
      });
    };

    window.addEventListener('online', updateNetworkStatus);
    window.addEventListener('offline', updateNetworkStatus);

    const connection = getConnection();
    if (connection && connection.addEventListener) {
      connection.addEventListener('change', updateNetworkStatus);
    }
    updateNetworkStatus();

    return () => {
      window.removeEventListener('online', updateNetworkStatus);
      window.removeEventListener('offline', updateNetworkStatus);
      if (connection && connection.removeEventListener) {
        connection.removeEventListener('change', updateNetworkStatus);
      }
    };
  }, []);

  return networkStatus;
};

/**
 * 💾 Hook для управления оффлайн данными
 */
const useOfflineStorage = (maxRetries: number = 3) => {
  const [offlineData, setOfflineData] = useState<OfflineData[]>([]);
  const [isSyncing, setIsSyncing] = useState(false);

  // Загрузка данных из localStorage при инициализации
  useEffect(() => {
    const stored = localStorage.getItem('offline_data');
    if (stored) {
      try {
        setOfflineData(JSON.parse(stored));
      } catch (error) {
        console.error('Failed to parse offline data:', error);
        localStorage.removeItem('offline_data');
      }
    }
  }, []);

  // Сохранение данных в localStorage при изменении
  useEffect(() => {
    if (offlineData.length > 0) {
      localStorage.setItem('offline_data', JSON.stringify(offlineData));
    } else {
      localStorage.removeItem('offline_data');
    }
  }, [offlineData]);

  const addOfflineData = (data: Omit<OfflineData, 'id' | 'timestamp' | 'retryCount' | 'maxRetries'>) => {
    const newData: OfflineData = {
      ...data,
      id: `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now(),
      retryCount: 0,
      maxRetries
    };

    setOfflineData(prev => [...prev, newData]);
    return newData.id;
  };

  const removeOfflineData = (id: string) => {
    setOfflineData(prev => prev.filter(item => item.id !== id));
  };

  const incrementRetryCount = (id: string) => {
    setOfflineData(prev => 
      prev.map(item => 
        item.id === id 
          ? { ...item, retryCount: item.retryCount + 1 }
          : item
      )
    );
  };

  const clearAllOfflineData = () => {
    setOfflineData([]);
  };

  return {
    offlineData,
    isSyncing,
    setIsSyncing,
    addOfflineData,
    removeOfflineData,
    incrementRetryCount,
    clearAllOfflineData
  };
};

// ===== ОСНОВНОЙ КОМПОНЕНТ =====

/**
 * 📱 Enterprise OfflineMode
 * 
 * Управление оффлайн режимом:
 * - Отслеживание статуса сети
 * - Кэширование данных оффлайн
 * - Автоматическая синхронизация
 * - Индикация качества соединения
 * - Управление очередью операций
 */
export const OfflineMode: React.FC<OfflineModeProps> = ({
  onSync,
  maxRetries = 3,
  syncInterval = 30000, // 30 секунд
  showDetails = false
}) => {
  const networkStatus = useNetworkStatus();
  const offlineStorage = useOfflineStorage(maxRetries);
  
  const [showOfflineAlert, setShowOfflineAlert] = useState(false);
  const [showDetailsPanel, setShowDetailsPanel] = useState(showDetails);
  const [lastSyncTime, setLastSyncTime] = useState<number>(0);

  // Показ/скрытие уведомления об оффлайн режиме
  useEffect(() => {
    if (!networkStatus.isOnline) {
      setShowOfflineAlert(true);
    } else if (networkStatus.isOnline && offlineStorage.offlineData.length > 0) {
      // Автоматическая синхронизация при восстановлении соединения
      handleSync();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [networkStatus.isOnline]);

  // Автоматическая синхронизация по интервалу
  useEffect(() => {
    if (!networkStatus.isOnline || offlineStorage.offlineData.length === 0) {
      return;
    }

    const interval = setInterval(() => {
      if (Date.now() - lastSyncTime > syncInterval) {
        handleSync();
      }
    }, syncInterval);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [networkStatus.isOnline, offlineStorage.offlineData.length, lastSyncTime, syncInterval]);

  const handleSync = async () => {
    if (!networkStatus.isOnline || offlineStorage.isSyncing || offlineStorage.offlineData.length === 0) {
      return;
    }

    offlineStorage.setIsSyncing(true);

    try {
      if (onSync) {
        await onSync(offlineStorage.offlineData);
      }
      
      // Успешная синхронизация - очищаем данные
      offlineStorage.clearAllOfflineData();
      setLastSyncTime(Date.now());
      
    } catch (error) {
      console.error('Sync failed:', error);
      
      // Увеличиваем счетчик попыток для каждого элемента
      offlineStorage.offlineData.forEach(item => {
        if (item.retryCount < item.maxRetries) {
          offlineStorage.incrementRetryCount(item.id);
        } else {
          // Удаляем элементы, превысившие лимит попыток
          offlineStorage.removeOfflineData(item.id);
        }
      });
    } finally {
      offlineStorage.setIsSyncing(false);
    }
  };

  const getConnectionQuality = (): { level: string; color: 'success' | 'warning' | 'error'; icon: React.ReactElement } => {
    if (!networkStatus.isOnline) {
      return { level: 'Оффлайн', color: 'error', icon: <OfflineIcon /> };
    }

    const { effectiveType, downlink } = networkStatus;
    
    if (effectiveType === '4g' || (downlink && downlink > 1.5)) {
      return { level: 'Отличное', color: 'success', icon: <OnlineIcon /> };
    } else if (effectiveType === '3g' || (downlink && downlink > 0.5)) {
      return { level: 'Хорошее', color: 'warning', icon: <OnlineIcon /> };
    } else {
      return { level: 'Слабое', color: 'error', icon: <OnlineIcon /> };
    }
  };

  const connectionQuality = getConnectionQuality();

  return (
    <>
      {/* Уведомление об оффлайн режиме */}
      <Snackbar
        open={showOfflineAlert && !networkStatus.isOnline}
        onClose={() => setShowOfflineAlert(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          severity="warning" 
          onClose={() => setShowOfflineAlert(false)}
          action={
            <Button 
              color="inherit" 
              size="small" 
              onClick={() => setShowDetailsPanel(!showDetailsPanel)}
            >
              Детали
            </Button>
          }
        >
          <Box display="flex" alignItems="center" gap={1}>
            <OfflineIcon />
            <Typography variant="body2">
              Нет подключения к интернету. Данные будут синхронизированы при восстановлении связи.
            </Typography>
          </Box>
        </Alert>
      </Snackbar>

      {/* Индикатор синхронизации */}
      {offlineStorage.isSyncing && (
        <Box position="fixed" top={0} left={0} right={0} zIndex={9999}>
          <LinearProgress />
          <Alert severity="info" sx={{ borderRadius: 0 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <SyncIcon className="animate-spin" />
              <Typography variant="body2">
                Синхронизация данных... ({offlineStorage.offlineData.length} элементов)
              </Typography>
            </Box>
          </Alert>
        </Box>
      )}

      {/* Панель деталей оффлайн режима */}
      <Collapse in={showDetailsPanel}>
        <Paper elevation={2} sx={{ m: 2, p: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Статус подключения
            </Typography>
            <IconButton 
              size="small" 
              onClick={() => setShowDetailsPanel(false)}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Статус сети */}
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Chip
              icon={connectionQuality.icon}
              label={`Соединение: ${connectionQuality.level}`}
              color={connectionQuality.color}
              variant="outlined"
            />
            
            {networkStatus.isOnline && (
              <>
                {networkStatus.effectiveType && (
                  <Chip
                    label={`Тип: ${networkStatus.effectiveType.toUpperCase()}`}
                    size="small"
                    variant="outlined"
                  />
                )}
                {networkStatus.downlink && (
                  <Chip
                    label={`Скорость: ${networkStatus.downlink} Mbps`}
                    size="small"
                    variant="outlined"
                  />
                )}
              </>
            )}
          </Box>

          {/* Оффлайн данные */}
          {offlineStorage.offlineData.length > 0 && (
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle1">
                  Данные для синхронизации ({offlineStorage.offlineData.length})
                </Typography>
                <Button
                  size="small"
                  startIcon={<SyncIcon />}
                  onClick={handleSync}
                  disabled={!networkStatus.isOnline || offlineStorage.isSyncing}
                >
                  Синхронизировать
                </Button>
              </Box>

              <List dense>
                {offlineStorage.offlineData.map((item) => (
                  <ListItem key={item.id}>
                    <ListItemIcon>
                      {item.retryCount >= item.maxRetries ? (
                        <WarningIcon color="error" />
                      ) : (
                        <CloudOffIcon color="action" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={`${item.type.toUpperCase()} ${item.entity}`}
                      secondary={
                        <Box>
                          <Typography variant="caption" display="block">
                            {new Date(item.timestamp).toLocaleString()}
                          </Typography>
                          {item.retryCount > 0 && (
                            <Typography variant="caption" color="warning.main">
                              Попыток: {item.retryCount}/{item.maxRetries}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => offlineStorage.removeOfflineData(item.id)}
                      >
                        <CloseIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* Информация о последней синхронизации */}
          {lastSyncTime > 0 && (
            <Box mt={2} p={1} bgcolor="success.light" borderRadius={1}>
              <Typography variant="caption" display="flex" alignItems="center" gap={1}>
                <CloudDoneIcon fontSize="small" />
                Последняя синхронизация: {new Date(lastSyncTime).toLocaleString()}
              </Typography>
            </Box>
          )}

          {/* Советы по оффлайн работе */}
          {!networkStatus.isOnline && (
            <Box mt={2} p={2} bgcolor="info.light" borderRadius={1}>
              <Typography variant="subtitle2" gutterBottom>
                💡 Советы для работы оффлайн:
              </Typography>
              <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                <li>Ваши изменения сохраняются локально</li>
                <li>Данные синхронизируются при восстановлении связи</li>
                <li>Избегайте закрытия браузера до синхронизации</li>
                <li>Проверьте подключение к Wi-Fi или мобильным данным</li>
              </Typography>
            </Box>
          )}
        </Paper>
      </Collapse>

      {/* Плавающий индикатор статуса */}
      {!showDetailsPanel && (offlineStorage.offlineData.length > 0 || !networkStatus.isOnline) && (
        <Box
          position="fixed"
          bottom={16}
          right={16}
          zIndex={1000}
        >
          <Chip
            icon={connectionQuality.icon}
            label={
              !networkStatus.isOnline 
                ? 'Оффлайн' 
                : `${offlineStorage.offlineData.length} для синхронизации`
            }
            color={connectionQuality.color}
            onClick={() => setShowDetailsPanel(true)}
            sx={{ cursor: 'pointer' }}
          />
        </Box>
      )}
    </>
  );
};

// ===== УТИЛИТЫ =====

/**
 * 💾 Утилита для добавления данных в оффлайн очередь
 */
export const addToOfflineQueue = (
  type: 'create' | 'update' | 'delete',
  entity: string,
  data: any
) => {
  const stored = localStorage.getItem('offline_data');
  const offlineData: OfflineData[] = stored ? JSON.parse(stored) : [];
  
  const newData: OfflineData = {
    id: `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    type,
    entity,
    data,
    timestamp: Date.now(),
    retryCount: 0,
    maxRetries: 3
  };

  offlineData.push(newData);
  localStorage.setItem('offline_data', JSON.stringify(offlineData));
  
  return newData.id;
};

/**
 * 🔄 Утилита для проверки статуса сети
 */
export const isOnline = (): boolean => {
  return navigator.onLine;
};

/**
 * 📊 Утилита для получения информации о соединении
 */
export const getConnectionInfo = () => {
  const connection = getConnection();

  return {
    isOnline: navigator.onLine,
    type: connection?.type,
    effectiveType: connection?.effectiveType,
    downlink: connection?.downlink,
    rtt: connection?.rtt
  };
};

export default OfflineMode;
